// @ts-check

/**
 * BOOP conversion analytics — PostHog event capture via document-level
 * delegation so every handler survives cart-drawer morphs without re-binding
 * and nothing double-binds. No imports: this stays independent of the theme
 * import map and parses standalone.
 *
 * All captures are best-effort. Handler bodies never call preventDefault, never
 * block navigation, and swallow their own errors so a tracking fault can never
 * break a customer action. Only ids, handles, sizes, totals, counts, booleans
 * and source strings are sent — never PII.
 */

/** @type {string | null} Dedupe key ("cart_total|threshold") of the last captured progress view. */
let lastProgressKey = null;

/** @type {IntersectionObserver | null} */
let progressObserver = null;

/**
 * Sends a PostHog event. Safe before init (the stub queues calls) and if PostHog never loads.
 * @param {string} name
 * @param {Record<string, unknown>} props
 */
function capture(name, props) {
  try {
    window.posthog?.capture?.(name, props);
  } catch {
    /* never let tracking break the page */
  }
}

/**
 * Reads a dataset value, returning undefined for missing/empty so it is omitted rather than sent as "".
 * @param {HTMLElement} el
 * @param {string} key - camelCase dataset key
 * @returns {string | undefined}
 */
function attr(el, key) {
  const value = el.dataset[key];
  return value == null || value === '' ? undefined : value;
}

/**
 * Reads a dataset value as a finite number, or undefined.
 * @param {HTMLElement} el
 * @param {string} key
 * @returns {number | undefined}
 */
function numAttr(el, key) {
  const value = attr(el, key);
  if (value === undefined) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Returns a copy of props with undefined values dropped.
 * @param {Record<string, unknown>} props
 * @returns {Record<string, unknown>}
 */
function clean(props) {
  /** @type {Record<string, unknown>} */
  const out = {};
  for (const [key, value] of Object.entries(props)) {
    if (value !== undefined) out[key] = value;
  }
  return out;
}

/**
 * Delegated tracking for elements carrying a `data-boop-track` intent.
 * @param {Element} target
 */
function handleTracked(target) {
  const el = target.closest('[data-boop-track]');
  if (!(el instanceof HTMLElement)) return;

  const base = { product_id: attr(el, 'productId'), handle: attr(el, 'productHandle') };

  if (el.dataset.boopTrack === 'fit_help') {
    capture('boop_pdp_fit_help_clicked', clean({ ...base, variant_id: attr(el, 'variantId'), selected_size: attr(el, 'selectedSize'), source: attr(el, 'source') }));
  } else if (el.dataset.boopTrack === 'policy_click') {
    capture('boop_pdp_policy_clicked', clean({ ...base, variant_id: attr(el, 'variantId'), selected_size: attr(el, 'selectedSize'), policy: attr(el, 'policy'), source: attr(el, 'source') }));
  } else if (el.dataset.boopTrack === 'complementary_click') {
    capture('boop_complementary_product_clicked', clean({ ...base, paired_product_id: attr(el, 'pairedProductId'), paired_product_handle: attr(el, 'pairedProductHandle'), source: attr(el, 'source') }));
  }
}

/**
 * Complementary clicks from the PDP "Pair It With" block, whose shared product
 * cards carry no `data-boop-*`. Identified via the complementary recommendations
 * host (`product-recommendations[data-intent="complementary"]`).
 * @param {Element} target
 */
function handleComplementaryCard(target) {
  const recs = target.closest('product-recommendations[data-intent="complementary"]');
  if (!(recs instanceof HTMLElement)) return;
  const card = target.closest('product-card');
  if (!(card instanceof HTMLElement)) return;

  const link = card.querySelector('a[href*="/products/"]');
  const href = link instanceof HTMLAnchorElement ? link.href : '';
  const match = href.match(/\/products\/([^/?#]+)/);

  capture('boop_complementary_product_clicked', clean({
    product_id: attr(recs, 'productId'),
    handle: attr(recs, 'productHandle'),
    paired_product_id: attr(card, 'productId'),
    paired_product_handle: match ? match[1] : undefined,
    source: 'pdp',
  }));
}

/**
 * Cart-drawer checkout start (drawer only — the /cart page renders the same
 * checkout button outside `cart-drawer-component` and must not be tracked).
 * Progress attrs are read when present; tracking still fires (with fewer
 * props) when the progress element is absent.
 * @param {Element} target
 */
function handleCheckout(target) {
  const btn = target.closest('#checkout, [name="checkout"]');
  if (!btn || !btn.closest('cart-drawer-component')) return;
  const progress = document.querySelector('[data-boop-progress]');
  const p = progress instanceof HTMLElement ? progress : null;
  capture('boop_checkout_started', clean({
    cart_total: p ? numAttr(p, 'cartTotal') : undefined,
    threshold: p ? numAttr(p, 'threshold') : undefined,
    line_count: p ? numAttr(p, 'lineCount') : undefined,
    source: 'cart_drawer',
  }));
}

/**
 * Captures a progress view once per distinct cart state while the drawer is open.
 * @param {HTMLElement} el
 */
function captureProgressViewed(el) {
  const cartTotal = numAttr(el, 'cartTotal');
  const threshold = numAttr(el, 'threshold');
  const key = `${cartTotal}|${threshold}`;
  if (key === lastProgressKey) return;
  lastProgressKey = key;
  capture('boop_cart_progress_viewed', clean({ cart_total: cartTotal, threshold, qualified: attr(el, 'qualified') === 'true', source: 'cart_drawer' }));
}

/** (Re)attaches the observer to the current progress element after a morph or drawer open. */
function observeProgress() {
  const el = document.querySelector('[data-boop-progress]');
  if (!progressObserver || !(el instanceof HTMLElement)) return;
  progressObserver.disconnect();
  progressObserver.observe(el);
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  try {
    handleTracked(target);
    handleComplementaryCard(target);
    handleCheckout(target);
  } catch {
    /* swallow — tracking must never break a click */
  }
});

if ('IntersectionObserver' in window) {
  progressObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting && entry.target instanceof HTMLElement) captureProgressViewed(entry.target);
    }
  }, { threshold: 0.5 });
  observeProgress();
  document.addEventListener('cart:update', observeProgress);
  document.addEventListener('toggle', (event) => {
    if (event.target instanceof HTMLDialogElement && event.target.open) observeProgress();
  }, true);
}
